import {supabase} from '../lib/supabase';
import {sendEmail,storeEmailInQueue} from './emailService';
import {collectUserAnalytics,formatAnalyticsForEmail,captureIPAddress} from './analyticsService';

/**
 * Formats an order into a simple,kitchen-friendly HTML email
 * @param {Object} orderData - The order data from the form
 * @param {Object} orderRecord - The saved order record from the database
 * @param {Object} analytics - Optional analytics data to include
 * @returns {string} - Simple HTML formatted email for kitchen
 */
export const formatOrderEmail=(orderData,orderRecord,analytics=null)=> {
  // Format patty selection for kitchen - each on separate line
  const pattyList=orderData.productType==='burger'
    ? orderData.pattys
      .map(patty=> {
        const pattyOption=orderData.pattyOptions.find(p=> p.id===patty.id);
        return `${patty.count}x ${pattyOption.name}`;
      })
      .join('<br>')
    : '';

  // Format pizza base selection
  const baseList=orderData.productType==='pizza'
    ? orderData.bases
      .map(base=> {
        const baseOption=orderData.baseOptions.find(b=> b.id===base.id);
        return `${base.count}x ${baseOption.name}`;
      })
      .join('<br>')
    : '';

  // Format toppings for kitchen - each on separate line
  const toppingsList=Object.entries(orderData.toppings)
    .filter(([id,data])=> data && data.count > 0)
    .map(([id,data])=> {
      const topping=Array.isArray(orderData.toppingOptions) ? orderData.toppingOptions.find(t=> t.id===id) : {name: id};
      return `${data.count}x ${topping ? topping.name : id}`;
    })
    .join('<br>');

  // Format sauces for kitchen - each on separate line
  const saucesList=Object.entries(orderData.sauces)
    .filter(([id,data])=> data && data.count > 0)
    .map(([id,data])=> {
      const sauce=Array.isArray(orderData.sauceOptions) ? orderData.sauceOptions.find(s=> s.id===id) : {name: id};
      // Add spicy level if applicable
      let sauceName=sauce ? sauce.name : id;
      if (sauce?.isSpicy && orderData.spicyLevels && orderData.spicyLevels[id]) {
        const spicyLevel=orderData.spicyLevels[id];
        const spicyLevelName=orderData.spicyLevelOptions ? orderData.spicyLevelOptions.find(l=> l.level===spicyLevel)?.name : `Schärfegrad ${spicyLevel}`;
        sauceName +=` (${spicyLevelName})`;
      }
      return `${data.count}x ${sauceName}`;
    })
    .join('<br>');

  // Format sides for kitchen - each on separate line
  const sidesList=Object.entries(orderData.sides)
    .filter(([id,data])=> data && data.count > 0)
    .map(([id,data])=> {
      const side=Array.isArray(orderData.sideOptions) ? orderData.sideOptions.find(s=> s.id===id) : {name: id};
      return `${data.count}x ${side ? side.name : id}`;
    })
    .join('<br>');

  // Format side sauces for kitchen
  const sideSaucesList=Object.entries(orderData.sideSauces || {})
    .filter(([id,data])=> data && data.count > 0)
    .map(([id,data])=> {
      const sauce=Array.isArray(orderData.sauceOptions) ? orderData.sauceOptions.find(s=> s.id===id) : {name: id};
      return `${data.count}x ${sauce ? sauce.name : id}`;
    })
    .join('<br>');

  // Get selected bun name
  const selectedBun=orderData.productType==='burger' && orderData.bunOptions ? orderData.bunOptions.find(b=> b.id===orderData.bun) : null;
  const bunName=selectedBun ? selectedBun.name : 'Nicht angegeben';

  // Order details
  const orderType=orderData.orderType==='pickup'
    ? `ABHOLUNG - ${orderData.pickupTime || 'Zeit nicht angegeben'}`
    : `LIEFERUNG - ${orderData.deliveryAddress || 'Adresse nicht angegeben'}`;

  // Format analytics data if provided (only for restaurant)
  const analyticsHtml=analytics ? formatAnalyticsForEmail(analytics) : '';

  // Create simple kitchen-style email
  return `
    <div style="font-family: 'Courier New',monospace;font-size: 14px;line-height: 1.4;max-width: 400px;background: white;padding: 20px;border: 2px solid #000;">
      <div style="text-align: center;border-bottom: 2px solid #000;padding-bottom: 10px;margin-bottom: 15px;">
        <h1 style="margin: 0;font-size: 18px;font-weight: bold;">${orderData.productType==='burger' ? '🍔 WUNSCH-BURGER' : '🍕 WUNSCH-PIZZA'}</h1>
        <h2 style="margin: 5px 0 0 0;font-size: 16px;">NEUE BESTELLUNG</h2>
      </div>
      <div style="margin-bottom: 15px;">
        <strong>BESTELL-NR:</strong> #${orderRecord.id.substring(0,8).toUpperCase()}<br>
        <strong>ZEIT:</strong> ${new Date().toLocaleString('de-DE')}<br>
        <strong>KUNDE:</strong> ${orderData.name}<br>
        <strong>TELEFON:</strong> ${orderData.phone}
      </div>
      <div style="border-top: 1px solid #000;border-bottom: 1px solid #000;padding: 10px 0;margin: 15px 0;">
        <strong>${orderType}</strong>
      </div>
      <div style="margin-bottom: 15px;">
        <h3 style="margin: 0 0 8px 0;font-size: 14px;text-decoration: underline;">${orderData.productType==='burger' ? 'BURGER' : 'PIZZA'}:</h3>
        ${orderData.productType==='burger'
      ? `
          <strong>PATTIES:</strong><br>
          <div style="margin-left: 10px;margin-bottom: 8px;">
            ${pattyList || 'Keine Patties'}
          </div>
          <strong>BRÖTCHEN:</strong><br>
          <div style="margin-left: 10px;margin-bottom: 8px;">
            ${bunName}
          </div>
        `
      : `
          <strong>BASIS:</strong><br>
          <div style="margin-left: 10px;margin-bottom: 8px;">
            ${baseList || 'Keine Basis'}
          </div>
          <strong>GRÖßE:</strong><br>
          <div style="margin-left: 10px;margin-bottom: 8px;">
            ${orderData.size==='small' ? 'Klein (26cm)' : orderData.size==='medium' ? 'Mittel (30cm)' : 'Groß (36cm)'}
          </div>
        `}
        ${toppingsList ? `
          <strong>TOPPINGS:</strong><br>
          <div style="margin-left: 10px;margin-bottom: 8px;">
            ${toppingsList}
          </div>
        ` : ''}
        ${saucesList ? `
          <strong>SAUCEN:</strong><br>
          <div style="margin-left: 10px;margin-bottom: 8px;">
            ${saucesList}
          </div>
        ` : ''}
      </div>

      ${sidesList ? `
        <div style="margin-bottom: 15px;">
          <h3 style="margin: 0 0 8px 0;font-size: 14px;text-decoration: underline;">BEILAGEN:</h3>
          <div style="margin-left: 10px;">
            ${sidesList}
          </div>
        </div>
      ` : ''}

      ${sideSaucesList ? `
        <div style="margin-bottom: 15px;">
          <h3 style="margin: 0 0 8px 0;font-size: 14px;text-decoration: underline;">SAUCEN FÜR BEILAGEN:</h3>
          <div style="margin-left: 10px;">
            ${sideSaucesList}
          </div>
        </div>
      ` : ''}

      ${orderData.specialRequests ? `
        <div style="margin-bottom: 15px;border: 1px solid #000;padding: 8px;">
          <strong>⚠️ SPEZIELLE WÜNSCHE:</strong><br>
          <div style="font-weight: bold;margin-top: 5px;">
            ${orderData.specialRequests}
          </div>
        </div>
      ` : ''}

      <div style="border-top: 2px solid #000;padding-top: 10px;text-align: center;">
        <strong style="font-size: 16px;">TOTAL: ${orderData.totalPrice.toFixed(2)} CHF</strong>
        <div style="margin-top: 8px;font-size: 12px;">
          E-Mail: ${orderData.email}
        </div>
      </div>
      ${analyticsHtml}
    </div>
  `;
};

/**
 * Creates customer confirmation email HTML
 * @param {Object} orderData - The order data
 * @param {Object} orderRecord - The order record from database
 * @returns {string} - HTML formatted confirmation email
 */
export const formatCustomerConfirmationEmail=(orderData,orderRecord)=> {
  // Format patty selection for customer
  const pattyList=orderData.productType==='burger'
    ? orderData.pattys
      .map(patty=> {
        const pattyOption=orderData.pattyOptions.find(p=> p.id===patty.id);
        return `<li>${pattyOption.name} x${patty.count}</li>`;
      })
      .join('')
    : '';

  // Format pizza base selection
  const baseList=orderData.productType==='pizza'
    ? orderData.bases
      .map(base=> {
        const baseOption=orderData.baseOptions.find(b=> b.id===base.id);
        return `<li>${baseOption.name} x${base.count}</li>`;
      })
      .join('')
    : '';

  // Format toppings for customer
  const toppingsList=Object.entries(orderData.toppings)
    .filter(([id,data])=> data && data.count > 0)
    .map(([id,data])=> {
      const topping=Array.isArray(orderData.toppingOptions) ? orderData.toppingOptions.find(t=> t.id===id) : {name: id};
      return `<li>${topping ? topping.name : id} x${data.count}</li>`;
    })
    .join('');

  // Format sauces for customer
  const saucesList=Object.entries(orderData.sauces)
    .filter(([id,data])=> data && data.count > 0)
    .map(([id,data])=> {
      const sauce=Array.isArray(orderData.sauceOptions) ? orderData.sauceOptions.find(s=> s.id===id) : {name: id};
      // Add spicy level if applicable
      let sauceName=sauce ? sauce.name : id;
      if (sauce?.isSpicy && orderData.spicyLevels && orderData.spicyLevels[id]) {
        const spicyLevel=orderData.spicyLevels[id];
        const spicyLevelName=orderData.spicyLevelOptions ? orderData.spicyLevelOptions.find(l=> l.level===spicyLevel)?.name : `Schärfegrad ${spicyLevel}`;
        sauceName +=` (${spicyLevelName})`;
      }
      return `<li>${sauceName} x${data.count}</li>`;
    })
    .join('');

  // Format sides for customer
  const sidesList=Object.entries(orderData.sides)
    .filter(([id,data])=> data && data.count > 0)
    .map(([id,data])=> {
      const side=Array.isArray(orderData.sideOptions) ? orderData.sideOptions.find(s=> s.id===id) : {name: id};
      return `<li>${side ? side.name : id} x${data.count}</li>`;
    })
    .join('');

  // Format side sauces for customer
  const sideSaucesList=Object.entries(orderData.sideSauces || {})
    .filter(([id,data])=> data && data.count > 0)
    .map(([id,data])=> {
      const sauce=Array.isArray(orderData.sauceOptions) ? orderData.sauceOptions.find(s=> s.id===id) : {name: id};
      return `<li>${sauce ? sauce.name : id} x${data.count}</li>`;
    })
    .join('');

  // Get selected bun name
  const selectedBun=orderData.productType==='burger' && orderData.bunOptions ? orderData.bunOptions.find(b=> b.id===orderData.bun) : null;
  const bunName=selectedBun ? selectedBun.name : 'Nicht angegeben';

  return `
    <div style="font-family: Arial,sans-serif;max-width: 600px;margin: 0 auto;padding: 20px;border: 1px solid #eaeaea;border-radius: 5px;">
      <h2 style="color: #f97316;border-bottom: 2px solid #f97316;padding-bottom: 10px;">Ihre Bestellbestätigung #${orderRecord.id.substring(
    0,
    8
  )}</h2>
      <p>Hallo ${orderData.name},</p>
      <p>Wir haben Ihre individuelle ${orderData.productType==='burger' ? 'Burger' : 'Pizza'}-Bestellung erhalten und bereiten sie mit Sorgfalt zu.</p>

      <p><strong>Bestellart:</strong> ${orderData.orderType==='pickup' ? 'Abholung' : 'Lieferung'}</p>
      ${orderData.orderType==='pickup'
      ? `<p><strong>Abholzeit:</strong> ${orderData.pickupTime || 'Nicht angegeben'}</p>`
      : `<p><strong>Lieferadresse:</strong> ${orderData.deliveryAddress || 'Nicht angegeben'}</p>`
    }
      <p><strong>Gesamtpreis:</strong> ${orderData.totalPrice.toFixed(2)} CHF</p>

      <div style="margin: 20px 0;padding: 15px;background-color: #f0f9ff;border-radius: 5px;">
        <h3 style="color: #333;">Ihre Bestellung:</h3>
        ${orderData.productType==='burger'
      ? `
          <p><strong>Burger-Patties:</strong></p>
          <ul>${pattyList || '<li>Keine Patties ausgewählt</li>'}</ul>
          <p><strong>Brötchen:</strong> ${bunName}</p>
        `
      : `
          <p><strong>Pizza-Basis:</strong></p>
          <ul>${baseList || '<li>Keine Basis ausgewählt</li>'}</ul>
          <p><strong>Größe:</strong> ${orderData.size==='small' ? 'Klein (26cm)' : orderData.size==='medium' ? 'Mittel (30cm)' : 'Groß (36cm)'}</p>
        `}
        ${toppingsList ? `
          <p><strong>Toppings:</strong></p>
          <ul>${toppingsList}</ul>
        ` : ''}
        ${saucesList ? `
          <p><strong>Saucen:</strong></p>
          <ul>${saucesList}</ul>
        ` : ''}
        ${sidesList ? `
          <p><strong>Beilagen:</strong></p>
          <ul>${sidesList}</ul>
        ` : ''}
        ${sideSaucesList ? `
          <p><strong>Saucen für Beilagen:</strong></p>
          <ul>${sideSaucesList}</ul>
        ` : ''}
      </div>
      <p>Wir informieren Sie,sobald Ihre Bestellung fertig ist!</p>
      <p>Bei Fragen erreichen Sie uns unter:</p>
      <p>📞 Telefon: +41 78 656 6575</p>
      <p>📧 E-Mail: andreas.bolli@icloud.com</p>
      <p>Vielen Dank,dass Sie sich für ${orderData.productType==='burger' ? 'Wunsch-Burger' : 'Wunsch-Pizza'} entschieden haben.</p>
    </div>
  `;
};


/**
 * Tests the database connection
 * @returns {Promise<boolean>} - True if connected,false otherwise
 */
export const testConnection=async ()=> {
  try {
    const {data,error}=await supabase.from('burger_orders_hb47x29k1a').select('id').limit(1);
    return !error;
  } catch (error) {
    console.error('Datenbankverbindungstest fehlgeschlagen:',error);
    return false;
  }
};

/**
 * Creates a new order in the database and sends real emails automatically
 * @param {Object} orderData - The order data from the form
 * @returns {Promise<Object>} - The created order record
 */
export const createOrder=async (orderData)=> {
  try {
    console.log('🍔 Bestellung wird erstellt:',orderData);
    // Collect analytics data
    const analytics=collectUserAnalytics();
    console.log('📊 Analytics-Daten gesammelt:',analytics);

    // Validate required fields
    if (!orderData.name || !orderData.phone || !orderData.email) {
      throw new Error('Pflichtfelder fehlen: Name,Telefon und E-Mail sind erforderlich.');
    }
    // Validate product-specific required fields
    if (orderData.productType==='burger') {
      if (!orderData.pattys || orderData.pattys.length===0) {
        throw new Error('Mindestens ein Patty muss ausgewählt werden.');
      }
    } else if (orderData.productType==='pizza') {
      if (!orderData.bases || orderData.bases.length===0) {
        throw new Error('Mindestens eine Pizza-Basis muss ausgewählt werden.');
      }
    }
    if (!orderData.totalPrice || orderData.totalPrice <=0) {
      throw new Error('Ungültiger Gesamtpreis.');
    }

    // Prepare data for database with proper JSON stringification
    const orderRecord={
      customer_name: orderData.name.trim(),
      customer_phone: orderData.phone.trim(),
      customer_email: orderData.email.trim().toLowerCase(),
      product_type: orderData.productType || 'burger',
      order_type: orderData.orderType,
      pickup_time: orderData.pickupTime || null,
      delivery_address: orderData.deliveryAddress || null,
      pattys: JSON.stringify(orderData.pattys || []),
      bases: JSON.stringify(orderData.bases || []),
      size: orderData.size || 'medium',
      bun: orderData.bun || 'classic',
      toppings: JSON.stringify(orderData.toppings || {}),
      sauces: JSON.stringify(orderData.sauces || {}),
      spicy_levels: JSON.stringify(orderData.spicyLevels || {}),
      sides: JSON.stringify(orderData.sides || {}),
      side_sauces: JSON.stringify(orderData.sideSauces || {}),
      special_requests: orderData.specialRequests || '',
      total_price: parseFloat(orderData.totalPrice),
      order_status: 'pending',
      analytics_data: JSON.stringify(analytics || {}),
    };
    console.log('💾 Speichere Bestellung in Datenbank:',orderRecord);

    // Insert into database
    const {data,error}=await supabase.from('burger_orders_hb47x29k1a').insert([orderRecord]).select();

    if (error) {
      console.error('❌ Datenbankfehler:',error);
      throw new Error(`Datenbankfehler: ${error.message}`);
    }

    if (!data || data.length===0) {
      throw new Error('Bestellung konnte nicht gespeichert werden - keine Daten zurückgegeben.');
    }

    const savedOrder=data[0];
    console.log('✅ Bestellung erfolgreich gespeichert:',savedOrder);

    // Capture IP address using external service (this will update the analytics)
    try {
      const ipResult=await captureIPAddress(savedOrder.id);
      console.log('📊 IP-Adresse erfasst:',ipResult);
      // Update analytics with IP address
      if (ipResult && ipResult.ip) {
        analytics.ipAddress=ipResult.ip;
      }
    } catch (ipError) {
      console.error('❌ Fehler bei der IP-Erfassung:',ipError);
    }

    // Prepare options for email formatting
    const emailData={
      ...orderData,
      pattyOptions: orderData.pattyOptions || [],
      bunOptions: orderData.bunOptions || [],
      baseOptions: orderData.baseOptions || [],
      toppingOptions: Array.isArray(orderData.toppingOptions) ? orderData.toppingOptions : [],
      sauceOptions: Array.isArray(orderData.sauceOptions) ? orderData.sauceOptions : [],
      sideOptions: Array.isArray(orderData.sideOptions) ? orderData.sideOptions : [],
      spicyLevelOptions: Array.isArray(orderData.spicyLevelOptions) ? orderData.spicyLevelOptions : []
    };

    // Format emails - Include analytics only in restaurant email
    const restaurantEmailHtml=formatOrderEmail(emailData,savedOrder,analytics);
    const customerEmailHtml=formatCustomerConfirmationEmail(emailData,savedOrder);

    // Send emails automatically with Mailgun API
    try {
      // Send notification email to restaurant
      console.log('📧 Sende Küchenbestellung an Restaurant...');
      const restaurantResult=await sendEmail(
        'andreas.bolli@icloud.com',
        `${orderData.productType==='burger' ? '🍔' : '🍕'} KÜCHE - Bestellung #${savedOrder.id.substring(0,8)}`,
        restaurantEmailHtml,
        'order_notification'
      );
      console.log('📧 Küchen-E-Mail Ergebnis:',restaurantResult);

      // Send confirmation email to customer
      console.log('📧 Sende Bestätigungs-E-Mail an Kunden...');
      const customerResult=await sendEmail(
        orderData.email,
        `✅ Ihre ${orderData.productType==='burger' ? 'Wunsch-Burger' : 'Wunsch-Pizza'} Bestellbestätigung #${savedOrder.id.substring(0,8)}`,
        customerEmailHtml,
        'customer_confirmation'
      );
      console.log('📧 Kunden-E-Mail Ergebnis:',customerResult);
      console.log('✅ E-Mails erfolgreich verarbeitet');
    } catch (emailError) {
      console.error('❌ E-Mail-Versand fehlgeschlagen:',emailError);
      // Order is still returned even if email sending fails
    }

    return savedOrder;
  } catch (error) {
    console.error('❌ Fehler bei der Bestellerstellung:',error);
    throw error;
  }
};